// frontend/src/components/ProjectForm.js
// Componente de formulario para crear o editar un proyecto.
// VERSIÓN FINAL v3: Oculta campos de metadata específicos de MAP.
// ACTUALIZADO: Añadido campo 'Nombre del Proyecto' condicional para PoC y Proyectos de Implementación.

import React, { useState, useEffect, useMemo } from 'react';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { useTemplateStore } from '../store/templateStore';
import { useUIStore } from '../store/uiStore';
import LoadingSpinner from './LoadingSpinner';

function ProjectForm({ projectToEdit = null, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    clientId: '',
    initiativeTypeId: '',
    status: 'active',
    totalExpectedAmount: '',
    phaseAmounts: { OLA: '', MRA: '', MPA: '' },
    awsId: '',
    creatioId: '',
    potentialMigration: '',
    apnId: '',
    incentiveFocus: '',
    consentDate: '',
    startDate: '',
    closureDate: '',
    assignedTeam: '',
    assessmentTool: '',
    quickInsight: '',
    olaBusinessCase: '',
    definitionRoles: '',
    projectName: '', // NUEVO: Inicializar projectName
  });

  const [saleAmount, setSaleAmount] = useState(0);
  const [margin, setMargin] = useState(40);
  const [profit, setProfit] = useState(0);

  const { addProject, updateProject } = useProjectStore();
  const { clients, fetchClients } = useClientStore();
  const { initiativeTypes, fetchInitiativeTypes } = useTemplateStore();
  const { isLoading, setIsLoading, showTemporaryMessage } = useUIStore();

  const isEditing = !!projectToEdit;

  useEffect(() => {
    fetchClients();
    fetchInitiativeTypes();
  }, [fetchClients, fetchInitiativeTypes]);

  useEffect(() => {
    if (isEditing) {
      const initialPhaseAmounts = {};
      projectToEdit.phases?.forEach(phase => {
        initialPhaseAmounts[phase.name] = phase.expectedAmount !== null ? phase.expectedAmount.toString() : '';
      });

      setFormData({
        clientId: projectToEdit.clientId || '',
        initiativeTypeId: projectToEdit.initiativeTypeId || '',
        status: projectToEdit.status || 'active',
        totalExpectedAmount: projectToEdit.totalExpectedAmount?.toString() || '',
        phaseAmounts: initialPhaseAmounts,
        awsId: projectToEdit.awsId || '',
        creatioId: projectToEdit.creatioId || '',
        potentialMigration: projectToEdit.potentialMigration || '',
        apnId: projectToEdit.apnId || '',
        incentiveFocus: projectToEdit.incentiveFocus || '',
        consentDate: projectToEdit.consentDate ? new Date(projectToEdit.consentDate).toISOString().split('T')[0] : '',
        startDate: projectToEdit.startDate ? new Date(projectToEdit.startDate).toISOString().split('T')[0] : '',
        closureDate: projectToEdit.closureDate ? new Date(projectToEdit.closureDate).toISOString().split('T')[0] : '',
        assignedTeam: projectToEdit.assignedTeam || '',
        assessmentTool: projectToEdit.assessmentTool || '',
        quickInsight: projectToEdit.quickInsight || '',
        olaBusinessCase: projectToEdit.olaBusinessCase || '',
        definitionRoles: projectToEdit.definitionRoles || '',
        projectName: projectToEdit.projectName || '', // NUEVO: Cargar projectName al editar
      });
      if (projectToEdit.totalExpectedAmount) {
        setSaleAmount(parseFloat(projectToEdit.totalExpectedAmount));
      }
    }
  }, [projectToEdit, isEditing]);

  const selectedInitiativeName = useMemo(() => {
    return initiativeTypes.find(type => type.id === formData.initiativeTypeId)?.name || '';
  }, [formData.initiativeTypeId, initiativeTypes]);

  useEffect(() => {
    // Si el tipo de iniciativa NO es MAP, actualiza el Monto Esperado con el SaleAmount
    if (selectedInitiativeName && selectedInitiativeName !== 'MAP') {
      if (saleAmount > 0 && margin >= 0 && margin < 100) {
        const calculatedProfit = saleAmount * (1 - (margin / 100));
        setProfit(calculatedProfit);
      } else {
        setProfit(0);
      }
      setFormData(prev => ({ ...prev, totalExpectedAmount: saleAmount.toString() }));
    } else {
      // Si es MAP o no hay tipo seleccionado, asegura que projectName sea null/vacío
      // Esto es crucial para que no se envíe un projectName a un proyecto MAP.
      setFormData(prev => ({ ...prev, projectName: '' }));
    }
  }, [saleAmount, margin, selectedInitiativeName]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhaseAmountChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      phaseAmounts: { ...prev.phaseAmounts, [name]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.clientId || !formData.initiativeTypeId) {
      showTemporaryMessage('El cliente y el tipo de iniciativa son obligatorios.', 'error');
      return;
    }

    // Validación condicional del nombre de proyecto
    if (selectedInitiativeName !== 'MAP' && !formData.projectName?.trim()) {
        showTemporaryMessage('El nombre del proyecto es obligatorio para iniciativas PoC o de Implementación.', 'error');
        return;
    }

    setIsLoading(true);
    try {
      if (isEditing) {
        await updateProject(projectToEdit.id, formData);
      } else {
        await addProject(formData);
      }
      onSuccess();
    } catch (error) {
      // Errores ya manejados en el store
    } finally {
      setIsLoading(false);
    }
  };

  const formTitle = isEditing ? 'Editar Iniciativa' : 'Añadir Nueva Iniciativa';
  const submitButtonText = isEditing ? 'Guardar Cambios' : 'Añadir Iniciativa';
  const calculatedReportedValue = projectToEdit?.realizedAmount ? parseFloat(projectToEdit.realizedAmount).toFixed(2) : '0.00';

  // Determina si el campo projectName debe ser requerido
  const isProjectNameRequired = selectedInitiativeName !== 'MAP';

  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto my-8">
      <h2 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">{formTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="clientId" className="block text-sm font-medium text-gray-700">Cliente <span className="text-red-500">*</span></label>
            <select id="clientId" name="clientId" value={formData.clientId} onChange={handleChange} required disabled={isLoading || isEditing} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
              <option value="">Selecciona un cliente</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.organizationName}{client.opportunityNumber ? ` (${client.opportunityNumber})` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="initiativeTypeId" className="block text-sm font-medium text-gray-700">Tipo de Iniciativa <span className="text-red-500">*</span></label>
            <select id="initiativeTypeId" name="initiativeTypeId" value={formData.initiativeTypeId} onChange={handleChange} required disabled={isLoading || isEditing} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
              <option value="">Selecciona un tipo</option>
              {initiativeTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          {/* NUEVO: Campo Nombre del Proyecto, visible solo para PoC y Proyecto de Implementación */}
          {selectedInitiativeName && selectedInitiativeName !== 'MAP' && (
            <div className="md:col-span-2"> {/* Ocupa ambas columnas para mayor espacio */}
              <label htmlFor="projectName" className="block text-sm font-medium text-gray-700">
                Nombre del Proyecto {isProjectNameRequired && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                required={isProjectNameRequired}
                disabled={isLoading}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          )}
        </div>

        <div className="border-t pt-6 mt-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Información Financiera</h3>

          {selectedInitiativeName === 'MAP' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="totalExpectedAmount" className="block text-sm font-medium text-gray-700">Monto Total Esperado (MAP)</label>
                  <input type="number" step="0.01" id="totalExpectedAmount" name="totalExpectedAmount" value={formData.totalExpectedAmount} onChange={handleChange} disabled={isLoading} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div>
                  <label htmlFor="reportedValue" className="block text-sm font-medium text-gray-700">Valor Reportado (Calculado)</label>
                  <input type="text" id="reportedValue" value={`$${calculatedReportedValue}`} readOnly className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"/>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">Ingresa el monto esperado para cada fase. Estos se sumarán al "Monto Realizado" cuando la fase esté 100% completada.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {['OLA', 'MRA', 'MPA'].map((phaseName) => (
                  <div key={phaseName}>
                    <label htmlFor={`expectedAmount-${phaseName}`} className="block text-sm font-medium text-gray-700">Monto Esperado {phaseName}</label>
                    <input type="number" step="0.01" id={`expectedAmount-${phaseName}`} name={phaseName} value={formData.phaseAmounts[phaseName]} onChange={handlePhaseAmountChange} disabled={isLoading} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-md font-semibold text-gray-800 mb-3">Cálculo de Venta y Margen</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="saleAmount" className="block text-sm font-medium text-gray-700">Monto Total de Venta</label>
                  <input type="number" step="0.01" id="saleAmount" value={saleAmount} onChange={(e) => setSaleAmount(parseFloat(e.target.value) || 0)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div>
                  <label htmlFor="margin" className="block text-sm font-medium text-gray-700">Margen de Contribución (%)</label>
                  <input type="number" step="0.01" id="margin" value={margin} onChange={(e) => setMargin(parseFloat(e.target.value) || 0)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                </div>
                <div>
                  <label htmlFor="profit" className="block text-sm font-medium text-gray-700">Ganancia del Proyecto (Calculado)</label>
                  <input type="text" id="profit" value={`$${profit.toFixed(2)}`} readOnly className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"/>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t pt-6 mt-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Detalles y Metadata Adicional</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
            {/* --- Campos Comunes --- */}
            <div><label htmlFor="awsId" className="text-sm font-medium text-gray-700">AWS ID</label><input type="text" id="awsId" name="awsId" value={formData.awsId} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading} /></div>
            <div><label htmlFor="creatioId" className="text-sm font-medium text-gray-700">Creatio ID</label><input type="text" id="creatioId" name="creatioId" value={formData.creatioId} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading} /></div>
            <div><label htmlFor="apnId" className="text-sm font-medium text-gray-700">APN ID</label><input type="text" id="apnId" name="apnId" value={formData.apnId} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading} /></div>
            <div><label htmlFor="assignedTeam" className="text-sm font-medium text-gray-700">Equipo Asignado</label><select id="assignedTeam" name="assignedTeam" value={formData.assignedTeam} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="si">Sí</option><option value="no">No</option></select></div>
            <div><label htmlFor="startDate" className="text-sm font-medium text-gray-700">Fecha Inicio</label><input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading} /></div>
            <div><label htmlFor="closureDate" className="text-sm font-medium text-gray-700">Fecha Cierre Estimada</label><input type="date" id="closureDate" name="closureDate" value={formData.closureDate} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading} /></div>
            <div><label htmlFor="definitionRoles" className="text-sm font-medium text-gray-700">Definición Roles</label><select id="definitionRoles" name="definitionRoles" value={formData.definitionRoles} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="si">Sí</option><option value="no">No</option></select></div>
            <div><label htmlFor="status" className="text-sm font-medium text-gray-700">Estado del Proyecto</label><select id="status" name="status" value={formData.status} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="active">Activo</option><option value="closed">Cerrado</option><option value="pending">Pendiente</option><option value="cancelled">Cancelado</option></select></div>

            {/* --- Campos Específicos de MAP --- */}
            {selectedInitiativeName === 'MAP' && (
              <>
                <div><label htmlFor="potentialMigration" className="text-sm font-medium text-gray-700">Potencial Migración</label><select id="potentialMigration" name="potentialMigration" value={formData.potentialMigration} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="si">Sí</option><option value="no">No</option><option value="tal vez">Tal vez</option></select></div>
                <div><label htmlFor="incentiveFocus" className="text-sm font-medium text-gray-700">Foco de Incentivo</label><select id="incentiveFocus" name="incentiveFocus" value={formData.incentiveFocus} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="OLA">OLA</option><option value="MRA">MRA</option><option value="MPA">MPA</option><option value="Todos">Todos</option></select></div>
                <div><label htmlFor="consentDate" className="text-sm font-medium text-gray-700">Fecha Consentimiento</label><input type="date" id="consentDate" name="consentDate" value={formData.consentDate} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading} /></div>
                <div><label htmlFor="assessmentTool" className="text-sm font-medium text-gray-700">Herramienta Assessment</label><select id="assessmentTool" name="assessmentTool" value={formData.assessmentTool} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="RV tools">RV tools</option><option value="Agente de AWS">Agente de AWS</option></select></div>
                <div><label htmlFor="quickInsight" className="text-sm font-medium text-gray-700">Quick Insight</label><select id="quickInsight" name="quickInsight" value={formData.quickInsight} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="si">Sí</option><option value="no">No</option></select></div>
                <div><label htmlFor="olaBusinessCase" className="text-sm font-medium text-gray-700">OLA Business Case</label><select id="olaBusinessCase" name="olaBusinessCase" value={formData.olaBusinessCase} onChange={handleChange} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" disabled={isLoading}><option value="">Seleccionar...</option><option value="si">Sí</option><option value="no">No</option></select></div>
              </>
            )}
          </div>
        </div>

        <div className="pt-6 flex justify-end space-x-4">
          <button type="button" onClick={onCancel} className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" disabled={isLoading}>
            Cancelar
          </button>
          <button type="submit" className={`px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" color="text-white" /> : submitButtonText}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProjectForm;